/**
 * Optional analytics handoff for an email signup confirmed in a DIFFERENT browser (Vidi-atl.22.3).
 *
 * Two halves, both strictly optional and never allowed to delay or fail auth:
 *  - Browser A (signs up): prepareSignupJourney() builds the context the signup request may carry
 *    and settleSignupJourney() keeps (or, after a withdrawal during the request, immediately uses) a
 *    withdrawal capability. prepare returns null unless analytics consent is granted AND the PostHog
 *    SDK is already loaded and anonymous AND the bounded revocation queue has room.
 *  - Browser B (opens the email link): handoffReceiptedSignup() spends the server's one-time claim
 *    and gives the result to posthogAnalytics.completeReceiptedSignup.
 *
 * Why a server claim and not browser state: B has none of A's sessionStorage/localStorage, and a
 * browser clock is not proof of a new account. The claim is backed by the immutable birth receipt
 * (supabase/migrations/20261003100549_signup_journey_handoff.sql).
 *
 * Withdrawal capability. A keeps a random bearer secret per journey. It is sent only to this app's
 * own Supabase signup edge function and revoke RPC over TLS (never PostHog, logs or artifacts); the
 * database stores only its SHA-256, so a database reader cannot revoke. Secrets live in ONE small
 * queue in localStorage, at most MAX_UNRESOLVED entries (live + revoke-only + requests still in
 * flight). Every mutation re-reads the queue AFTER any await and touches only its own secret, which
 * fixes same-page async clobbering (a revoke is awaited while another signup settles); a pending
 * revoke-only entry is never replaced by a newer signup. localStorage read-modify-write is NOT
 * atomic across tabs: cross-tab races stay best-effort. When the queue is full a NEW optional journey is refused (the signup
 * itself is unaffected); nothing grows without bound and no server tombstone exists.
 *
 * Honest limits: withdrawal reaches the server only if A is online when consent is withdrawn (it is
 * retried on later loads until it succeeds or the 3 day expiry passes); a revoke that beats the
 * server's store, an offline A, and a claim already consumed by B cannot be undone; a claim that
 * cannot be sent is one lost optional event; no step here proves PostHog ingested, merged or
 * deduplicated anything, and withdrawing never removes events PostHog already received.
 */
import { hasAnalyticsConsent } from '@/hooks/useConsent';
import { getStoredAttribution } from '@/utils/attribution';
import { APPROVED_CAMPAIGN_LABELS } from '@/utils/campaignLabels';
import {
  beginReceiptedSignupClaim,
  completeReceiptedSignup,
  currentConsentPeriod,
  currentAnalyticsOwnershipPeriod,
  isPosthogConfigured,
  readAnonymousJourneyId,
  type ReceiptedSignupClaim,
} from '@/services/posthogAnalytics';

/** The same 3 day bound the server uses for stored context and for "new signup" account age. */
const JOURNEY_LIFETIME_MS = 3 * 24 * 60 * 60 * 1000;
/** Unresolved capabilities (stored + in flight). Full means: refuse a NEW optional context. */
const MAX_UNRESOLVED = 5;
const REVOKE_KEY = 'vidi-signup-journey-revoke';
const REVOKE_FUNCTION = 'revoke_signup_journey_context';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type Rpc = (
  fn: string,
  args: Record<string, unknown>,
) => PromiseLike<{ data: unknown; error: unknown }>;

const label = (value: string | null | undefined, allowed: readonly string[]): string =>
  value && allowed.includes(value.toLowerCase()) ? value.toLowerCase() : 'unknown';

function randomSecret(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export type PreparedSignupJourney = {
  /** The signup request's optional `journey` field (validated again by the edge function). */
  body: {
    anonymousId: string;
    source: string;
    medium: string;
    campaign: string;
    placement: string;
    /**
     * Random bearer secret for best-effort withdrawal. It travels only to this app's own Supabase
     * signup edge function and revoke RPC over TLS, never to PostHog, a log or an artifact; the
     * database stores only its SHA-256, so a database reader cannot revoke.
     */
    revokeToken: string;
  };
  /** The same secret, kept in the local queue to revoke later. */
  revokeToken: string;
  /** The consent period this journey was sent under; see settleSignupJourney. */
  consentPeriod: number;
  ownershipPeriod: number;
};

/**
 * The withdrawal capability for one stored A-side journey, keyed by its secret. Never an analytics
 * value. `revokeOnly` marks an entry whose revoke must be retried whatever the consent state is now
 * (consent was withdrawn or another account verified, and delivery has not landed).
 */
type StoredRevocation = {
  userId: string;
  revokeToken: string;
  expiresAt: number;
  revokeOnly?: boolean;
};

// Signups that carried a journey and have not answered yet. While one is pending, a revoke that
// finds nothing (false) is NOT proof of absence: the server copy may be stored after the revoke.
let inFlightSignups = 0;
// Secrets with a revoke request in the air, so two triggers do not race on the same entry.
const revokingTokens = new Set<string>();
// Each prepared journey is settled exactly once, even if the caller settles twice.
const settledJourneys = new WeakSet<PreparedSignupJourney>();

function readQueue(): StoredRevocation[] {
  try {
    const raw = localStorage.getItem(REVOKE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    const queue: StoredRevocation[] = [];
    for (const item of parsed as Partial<StoredRevocation>[]) {
      if (
        typeof item?.userId === 'string' &&
        typeof item.revokeToken === 'string' &&
        typeof item.expiresAt === 'number' &&
        item.expiresAt > now &&
        !queue.some((e) => e.revokeToken === item.revokeToken)
      )
        queue.push({
          userId: item.userId,
          revokeToken: item.revokeToken,
          expiresAt: item.expiresAt,
          revokeOnly: item.revokeOnly === true,
        });
    }
    return queue.slice(0, MAX_UNRESOLVED);
  } catch {
    return [];
  }
}

/**
 * Read-modify-write on the CURRENT queue. Callers pass a change that touches only their own secret,
 * and run it AFTER any await, so a newer same-page signup or revoke entry is not overwritten. Cross-tab updates are not atomic.
 */
function mutateQueue(change: (queue: StoredRevocation[]) => StoredRevocation[]): void {
  try {
    const next = change(readQueue()).slice(0, MAX_UNRESOLVED);
    if (next.length) localStorage.setItem(REVOKE_KEY, JSON.stringify(next));
    else localStorage.removeItem(REVOKE_KEY);
  } catch {
    /* storage denied: withdrawal then relies on the server expiry alone */
  }
}

const removeEntry = (token: string) =>
  mutateQueue((queue) => queue.filter((e) => e.revokeToken !== token));
const markRevokeOnly = (token: string) =>
  mutateQueue((queue) =>
    queue.map((e) => (e.revokeToken === token ? { ...e, revokeOnly: true } : e)),
  );

/** Adds an entry without replacing any other; a known secret is only ever upgraded to revoke-only. */
function addEntry(entry: StoredRevocation): boolean {
  let stored = false;
  mutateQueue((queue) => {
    const existing = queue.find((e) => e.revokeToken === entry.revokeToken);
    if (existing) {
      stored = true;
      return entry.revokeOnly
        ? queue.map((e) => (e === existing ? { ...e, revokeOnly: true } : e))
        : queue;
    }
    if (queue.length >= MAX_UNRESOLVED) return queue;
    stored = true;
    return [...queue, entry];
  });
  return stored;
}

/**
 * Null (omit the field) unless consent is on, the SDK is ready and anonymous, and the queue has
 * room. Never waits. A full queue refuses only this OPTIONAL context: the signup is unaffected.
 */
export function prepareSignupJourney(): PreparedSignupJourney | null {
  try {
    if (!isPosthogConfigured() || !hasAnalyticsConsent()) return null;
    if (readQueue().length + inFlightSignups >= MAX_UNRESOLVED) return null;
    const anonymousId = readAnonymousJourneyId();
    if (!anonymousId) return null;
    const touch = getStoredAttribution();
    const revokeToken = randomSecret();
    inFlightSignups += 1;
    return {
      revokeToken,
      consentPeriod: currentConsentPeriod(),
      ownershipPeriod: currentAnalyticsOwnershipPeriod(),
      body: {
        anonymousId,
        source: label(touch?.utm_source, APPROVED_CAMPAIGN_LABELS.utm_source),
        medium: label(touch?.utm_medium, APPROVED_CAMPAIGN_LABELS.utm_medium),
        campaign: label(touch?.utm_campaign, APPROVED_CAMPAIGN_LABELS.utm_campaign),
        placement: label(touch?.utm_content, APPROVED_CAMPAIGN_LABELS.utm_content),
        revokeToken,
      },
    };
  } catch {
    return null;
  }
}

/** One delivery attempt. On failure a bounded revoke-only entry is kept so a later load retries. */
async function deliverRevocation(rpc: Rpc, entry: StoredRevocation): Promise<void> {
  try {
    const { error } = await rpc(REVOKE_FUNCTION, {
      p_user_id: entry.userId,
      p_token: entry.revokeToken,
    });
    if (!error) return;
  } catch {
    /* fall through to the retry entry */
  }
  addEntry({ ...entry, revokeOnly: true });
}

/**
 * Called exactly once for every prepared journey when its signup request ANSWERS or FAILS (extra
 * calls are ignored). The edge function stores the server copy before it responds, so the
 * capability is bound to the consent period the journey was sent under, not to whatever consent is
 * now:
 *  - consent unchanged: keep the capability (under consent only) for a later withdrawal;
 *  - consent withdrawn while the request was in flight (even if granted again since): the server
 *    copy may exist, so revoke exactly that capability now, and keep a bounded revoke-only entry if
 *    delivery fails. Nothing new is stored as analytics, and the signup itself is never affected.
 * A response without a usable account id (request failed, duplicate-email shape) leaves nothing to
 * revoke by: the server copy, if any, expires after 3 days. An actor change while the request is pending revokes the A-side journey, because its anonymous
 * ID may now identify another account. Revocation always uses the exact answered account and capability. Never throws.
 */
export function settleSignupJourney(
  rpc: Rpc,
  prepared: PreparedSignupJourney,
  userId: unknown,
): void {
  try {
    if (settledJourneys.has(prepared)) return;
    settledJourneys.add(prepared);
    inFlightSignups = Math.max(0, inFlightSignups - 1);
    if (typeof userId !== 'string' || !UUID.test(userId)) return;
    const entry: StoredRevocation = {
      userId,
      revokeToken: prepared.revokeToken,
      expiresAt: Date.now() + JOURNEY_LIFETIME_MS,
    };
    if (
      hasAnalyticsConsent() &&
      currentConsentPeriod() === prepared.consentPeriod &&
      currentAnalyticsOwnershipPeriod() === prepared.ownershipPeriod
    ) {
      // Capacity was reserved by prepare; if another writer still filled it, revoke right away
      // rather than hold a server copy this browser can no longer withdraw.
      if (!addEntry(entry)) void deliverRevocation(rpc, entry);
      return;
    }
    void deliverRevocation(rpc, entry);
  } catch {
    /* optional analytics can never fail signup */
  }
}

/**
 * Best-effort withdrawal. Call on load and whenever consent changes, with the current consent
 * state: an entry is revoked when consent is not granted, or when it is already revoke-only (kept
 * even if consent was granted again). Deletes only the optional server copy; B can still count the
 * signup once, with no link to A.
 * Each entry is removed only after the server answered `true`, or `false` with no signup pending;
 * both the removal and any revoke-only marking touch that entry's secret alone, after the await.
 * `false` while a signup is still in flight is NOT proof of absence (the copy may be stored after
 * the revoke), so the entry is kept revoke-only until expiry. A network failure also keeps it.
 * There is deliberately no server-side tombstone for a revoke that beats the store. Never throws.
 */
export async function revokeStoredSignupJourney(rpc: Rpc, consentGranted: boolean): Promise<void> {
  mutateQueue((queue) => queue); // readQueue already dropped expired entries; persist that pruning
  for (const entry of readQueue()) {
    if ((consentGranted && !entry.revokeOnly) || revokingTokens.has(entry.revokeToken)) continue;
    revokingTokens.add(entry.revokeToken);
    try {
      const { data, error } = await rpc(REVOKE_FUNCTION, {
        p_user_id: entry.userId,
        p_token: entry.revokeToken,
      });
      if (error || (data === false && inFlightSignups > 0)) markRevokeOnly(entry.revokeToken);
      else removeEntry(entry.revokeToken);
    } catch {
      markRevokeOnly(entry.revokeToken);
    } finally {
      revokingTokens.delete(entry.revokeToken);
    }
  }
}

/**
 * Browser A verified an account. A stored journey belongs to the account that signed up here; if the
 * verified account is a different one, or it cannot be established (missing or malformed id), the
 * anonymous id A sent could by now belong to someone else's profile, so every entry that does not
 * match is marked revoke-only and revoked now. Conservative on purpose: no guarantee of instant
 * cross-device revocation, and nothing already received by PostHog is removed. Never throws.
 */
export async function revokeForVerifiedAccount(
  rpc: Rpc,
  verifiedId: string | null | undefined,
): Promise<void> {
  try {
    const known =
      typeof verifiedId === 'string' && UUID.test(verifiedId) ? verifiedId.toLowerCase() : null;
    mutateQueue((queue) =>
      queue.map((e) =>
        known && e.userId.toLowerCase() === known ? e : { ...e, revokeOnly: true },
      ),
    );
    await revokeStoredSignupJourney(rpc, true);
  } catch {
    /* withdrawal is best effort */
  }
}

/** Server-verified sole email provider and identity: the only shape the birth receipt can prove. */
export function isVerifiedSoleEmail(user: {
  email_confirmed_at?: string | null;
  app_metadata?: Record<string, unknown> | null;
  identities?: Array<{ provider?: string }> | null;
}): boolean {
  const meta = user.app_metadata;
  if (!meta || !user.email_confirmed_at || meta.provider !== 'email') return false;
  if (
    !Array.isArray(meta.providers) ||
    meta.providers.length !== 1 ||
    meta.providers[0] !== 'email'
  )
    return false;
  return (
    Array.isArray(user.identities) &&
    user.identities.length === 1 &&
    user.identities[0]?.provider === 'email'
  );
}

/** Strict parse of the claim answer. A malformed journey is dropped; the claim itself still counts. */
export function parseSignupClaim(data: unknown): ReceiptedSignupClaim | null {
  if (typeof data !== 'object' || data === null) return null;
  const raw = data as Record<string, unknown>;
  if (raw.status !== 'claimed' || typeof raw.claim_id !== 'string' || !UUID.test(raw.claim_id))
    return null;
  const claimId = raw.claim_id.toLowerCase();
  const j = raw.journey;
  if (typeof j !== 'object' || j === null) return { claimId, journey: null };
  const journey = j as Record<string, unknown>;
  const lists = APPROVED_CAMPAIGN_LABELS;
  const closed = (value: unknown, allowed: readonly string[]) =>
    typeof value === 'string' && (value === 'unknown' || allowed.includes(value));
  if (
    typeof journey.anonymous_id !== 'string' ||
    !UUID.test(journey.anonymous_id) ||
    !closed(journey.source, lists.utm_source) ||
    !closed(journey.medium, lists.utm_medium) ||
    !closed(journey.campaign, lists.utm_campaign) ||
    !closed(journey.placement, lists.utm_content)
  )
    return { claimId, journey: null };
  return {
    claimId,
    journey: {
      anonymousId: journey.anonymous_id.toLowerCase(),
      source: journey.source as string,
      medium: journey.medium as string,
      campaign: journey.campaign as string,
      placement: journey.placement as string,
    },
  };
}

// One claim attempt per account per page load: the server claim is single-use, so a re-render or a
// token refresh must not spend it twice, and a failed attempt is not retried (lost optional event).
const attemptedClaims = new Set<string>();
export function resetSignupJourneyForTests(): void {
  attemptedClaims.clear();
  revokingTokens.clear();
  inFlightSignups = 0;
}

/**
 * Browser B, after the server verified the account as external. Only a fresh account whose
 * server-verified shape is a SOLE email provider and identity (the only shape the birth receipt can
 * prove) asks for the claim, so a mixed or Google account fails closed in the browser as well as in
 * the database. The claim is requested only when this browser may send it (consent, external,
 * current actor). Never throws.
 */
export async function handoffReceiptedSignup(
  rpc: Rpc,
  verified: { id: string; created_at: string },
  verifiedSoleEmail: boolean,
): Promise<void> {
  try {
    const created = Date.parse(verified.created_at);
    if (
      !verifiedSoleEmail ||
      !Number.isFinite(created) ||
      Date.now() - created > JOURNEY_LIFETIME_MS ||
      attemptedClaims.has(verified.id)
    )
      return;
    const ticket = beginReceiptedSignupClaim(verified.id);
    if (!ticket) return;
    attemptedClaims.add(verified.id);
    const { data, error } = await rpc('claim_new_signup_analytics', {
      p_expected_user_id: verified.id,
    });
    if (error) return;
    const claim = parseSignupClaim(data);
    if (claim) await completeReceiptedSignup(ticket, claim);
  } catch {
    /* optional analytics can never fail sign-in */
  }
}
